import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { getCookies, updateUserNameCookies } from '@pkg-services/cookies/Cookies';
import { ThemeService } from '@pkg-services/theme/theme.service';
import { AdminService } from '@pkg-services/admin/admin.service';
import { AdminActionCenterService } from '@pkg-services/admin/admin-action-center.service';
import { CookiesService } from '@pkg-services/cookies/cookies.service';
import { ApiCallService } from '@pkg-services/callApi/api-call.service';
import { RouteAdminLogout } from '@pkg-framework/models/admin';
import { IconsDirective } from '@pkg-directives/icons/icons.directive';
import { AdminActionListComponent } from '@app/shared/components/admin-action-list/admin-action-list.component';
import { UpcomingCallbacksComponent } from '@app/shared/components/upcoming-callbacks/upcoming-callbacks.component';
import type { AdminActionItem } from '@pkg-framework/models/admin';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, IconsDirective, AdminActionListComponent, UpcomingCallbacksComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isMenuOpen = true;
  @Output() toggleSidebar = new EventEmitter<void>();

  userMenuOpen = false;
  notificationsOpen = false;
  actionItems: AdminActionItem[] = [];
  notificationCount = 0;
  /** Icône téléphone : appels du jour + clients en attente ; menu des prochains appels. */
  callbacksCount = 0;
  clientCallbacks = 0;
  callbacksOpen = false;
  callbacksReload = 0;
  private sub?: Subscription;

  /** « Prénom / nom » du compte ; le sous-titre (fonction) est un champ à part. */
  get userName(): string {
    return (getCookies('user_name') || '').trim() || 'Super Admin';
  }

  get userSubName(): string {
    return (getCookies('user_subname') || '').trim();
  }

  /** Premier mot du nom, à côté de l'avatar (le nom complet reste dans le menu). */
  get userFirstName(): string {
    return this.userName.split(/\s+/)[0];
  }

  /** « Mon profil » (30/10/2026) : nom et sous-titre du compte connecté. */
  profileOpen = false;
  profileSaving = false;
  profileError = '';
  profile = { name: '', sub_name: '' };

  openProfile(): void {
    this.profile = { name: getCookies('user_name') || '', sub_name: this.userSubName };
    this.profileError = '';
    this.profileOpen = true;
    this.closeUserMenu();
  }

  saveProfile(): void {
    if (this.profileSaving) return;
    if (!this.profile.name.trim()) {
      this.profileError = 'Le nom est obligatoire.';
      return;
    }
    this.profileSaving = true;
    this.api.patch('super-admin/me', { name: this.profile.name.trim(), sub_name: this.profile.sub_name.trim() || null }).subscribe({
      next: (res: { data?: { admin?: { name?: string; sub_name?: string | null } } }) => {
        this.profileSaving = false;
        const a = res?.data?.admin;
        updateUserNameCookies(a?.name ?? this.profile.name.trim(), a?.sub_name ?? '');
        this.profileOpen = false;
      },
      error: (err: { error?: { message?: string; errors?: Record<string, string[]> } }) => {
        this.profileSaving = false;
        this.profileError = Object.values(err?.error?.errors ?? {})[0]?.[0] || err?.error?.message || 'Enregistrement impossible.';
      },
    });
  }

  get userEmail(): string {
    return getCookies('user_email') || '';
  }

  get userInitials(): string {
    const n = this.userName;
    if (!n) return 'A';
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return n.slice(0, 2).toUpperCase();
  }

  get isDark(): boolean {
    return this.theme.isDarkMode();
  }

  constructor(
    private theme: ThemeService,
    private adminService: AdminService,
    private actionCenter: AdminActionCenterService,
    private cookies: CookiesService,
    private api: ApiCallService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.sub = this.actionCenter.watch().subscribe((data) => {
      this.actionItems = data?.items ?? [];
      this.notificationCount = data?.total_urgent ?? 0;
      this.callbacksCount = data?.sidebar_counts?.callbacks_to_call ?? 0;
      this.clientCallbacks = data?.sidebar_counts?.client_callbacks ?? 0;
    });
    if (!this.actionCenter.snapshot()) {
      this.actionCenter.refresh().subscribe({ error: () => {} });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.userMenuOpen && target && !target.closest('.header-avatar-wrap')) {
      this.userMenuOpen = false;
    }
    if (this.notificationsOpen && target && !target.closest('.header-notif-wrap')) {
      this.notificationsOpen = false;
    }
    if (this.callbacksOpen && target && !target.closest('.header-calls-wrap')) {
      this.callbacksOpen = false;
    }
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  toggleTheme(): void {
    const next = this.theme.getCurrentTheme() === 'light' ? 'dark' : 'light';
    this.theme.applyTheme(next);
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    this.notificationsOpen = false;
  }

  toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
    this.userMenuOpen = false;
    if (this.notificationsOpen) {
      this.actionCenter.refresh().subscribe({ error: () => {} });
    }
  }

  get callsTitle(): string {
    const clients = this.clientCallbacks ? ` dont ${this.clientCallbacks} client${this.clientCallbacks > 1 ? 's' : ''}` : '';
    return `${this.callbacksCount} appel${this.callbacksCount > 1 ? 's' : ''} à passer${clients}`;
  }

  toggleCallbacks(): void {
    this.callbacksOpen = !this.callbacksOpen;
    this.notificationsOpen = false;
    this.userMenuOpen = false;
    if (this.callbacksOpen) this.callbacksReload++;
  }

  closeNotifications(): void {
    this.notificationsOpen = false;
  }

  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  goToActionCenter(): void {
    this.closeNotifications();
    this.router.navigate(['/admin/dashboard'], { fragment: 'actions' });
  }

  onLogout(): void {
    this.api.post(RouteAdminLogout, {}).subscribe({
      next: () => this.doLogout(),
      error: () => this.doLogout()
    });
  }

  private doLogout(): void {
    this.actionCenter.clear();
    this.adminService.clearCurrentAdmin();
    this.cookies.remove();
    this.router.navigate(['/login']);
  }
}
